import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

/**
 * SelectWithCreate - Dropdown con botón para crear nuevo item inline
 * 
 * Props:
 * - value: valor seleccionado actual
 * - onValueChange: callback cuando cambia el valor
 * - options: array de opciones [{id, nombre}]
 * - placeholder: texto del placeholder
 * - createEndpoint: URL del endpoint para crear (ej: '/config/sites' o '/config/catalogos/criticidad')
 * - createFields: config de campos {name: {label, placeholder}, description: {label, placeholder}}
 * - entityName: nombre para mostrar (ej: 'Site', 'Criticidad')
 * - onCreated: callback después de crear, recibe el nuevo item
 * - disabled: deshabilitar el select
 * - className: clases adicionales para el contenedor
 */
export function SelectWithCreate({
  value,
  onValueChange,
  options = [],
  placeholder = "Seleccionar...",
  createEndpoint,
  createFields = {
    name: { label: "Nombre", placeholder: "Nombre" },
    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
  },
  entityName = "Item",
  onCreated,
  disabled = false,
  className = "",
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error(`El nombre es requerido`);
      return;
    }

    setCreating(true);
    try {
      const response = await api.post(createEndpoint, {
        name: formData.name.trim(),
        description: formData.description.trim() || null
      });
      
      toast.success(`${entityName} creado exitosamente`);
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      
      // Callback para refrescar opciones y seleccionar el nuevo
      if (onCreated) {
        onCreated(response.data);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || `Error al crear ${entityName.toLowerCase()}`;
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDialogOpen(true);
  };

  return (
    <div className={`flex gap-1.5 items-center ${className}`}>
      <div className="flex-1">
        <Select value={value || "none"} onValueChange={(v) => onValueChange(v === "none" ? "" : v)} disabled={disabled}>
          <SelectTrigger className="input-subtle">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin seleccionar</SelectItem>
            {options.map(opt => (
              <SelectItem key={opt.id} value={String(opt.id)}>{opt.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {!disabled && createEndpoint && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 border-dashed hover:border-primary hover:text-primary"
          onClick={handleOpenDialog}
          title={`Crear nuevo ${entityName.toLowerCase()}`}
          data-testid={`create-${entityName.toLowerCase()}-btn`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Nuevo {entityName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">
                {createFields.name?.label || "Nombre"} *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={createFields.name?.placeholder || "Nombre"}
                className="input-subtle mt-1.5"
                autoFocus
                data-testid={`new-${entityName.toLowerCase()}-name`}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">
                {createFields.description?.label || "Descripción"}
              </Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={createFields.description?.placeholder || "Descripción (opcional)"}
                className="input-subtle mt-1.5"
                data-testid={`new-${entityName.toLowerCase()}-description`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SelectWithCreate;
